
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { singularityStore } from "@/store/store";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { step1RebuildFromUrl } from "@/lib/singularity/step1/step1";
import TopNav from "../TopNav/TopNav.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import {
  step1ButtonClicked,
  maybeThingsWeNeedToDoFirst
} from "@/futurelayer/generate-homepage";
import {
  FutureLayerAISettings,
  saveFutureLayerAISettingsToGlobalStoreAndWordPress,
  getFutureLayerAISettings
} from "@/futurelayer/settings/settings";
import Icon from "@/components/UI/Icon.vue";

type SamplePrompt = {
  buttonText: string;
  businessName: string;
  businessDescription: string;
};

export type NavItem = {
  title: string;
  isActive?: boolean;
};

@Component({
  components: {
    TextInput,
    TopNav,
    NiceButton,
    Icon
  }
})
export default class SitemapGenerator extends Vue {
  creating = false;
  rebuildFromUrl = null;
  waitingForThingsToBeReadyDone = false;
  isBuildFromUrlMode = false;
  activeStep = 0;

  // Internal state for AI settings
  aiSettings: FutureLayerAISettings = {
    title: "",
    description: ""
  };

  promptSteps: NavItem[] = [
    {
      title: "Start"
    },
    {
      title: "Category"
    },
    {
      title: "Goals"
    },
    {
      title: "Writing Style"
    }
  ];

  samplePrompts: SamplePrompt[] = [
    {
      buttonText: "Landing Page",
      businessName: "Smart Widget",
      businessDescription:
        "Create a landing page for a new product called Smart Widget. It should highlight the features and benefits of the product."
    },
    {
      buttonText: "Blog",
      businessName: "Travel Adventures",
      businessDescription:
        "Create a blog page called Travel Adventures which focuses on travel tips and experiences."
    },
    {
      buttonText: "Portfolio",
      businessName: "Creative Minds",
      businessDescription:
        "Create a design portfolio for agency called Creative Minds. It should showcase their best work and include client testimonials."
    },
    {
      buttonText: "Small Business",
      businessName: "Local Bakery",
      businessDescription:
        "Create a website for a small business called Local Bakery. It should include information about their products, location, and hours of operation."
    }
  ];

  generate() {
    if (this.isBuildFromUrlMode) {
      this.rebuild();
    } else {
      this.create();
    }
  }

  async mounted() {
    await maybeThingsWeNeedToDoFirst();
    this.aiSettings = getFutureLayerAISettings();
    this.waitingForThingsToBeReadyDone = true;
    this.checkForAutostart();
  }

  checkForAutostart() {
    if (this.shouldAutostart && this.aiSettings.title && this.aiSettings.description) {
      this.generate();
    }
  }

  async create() {
    this.creating = true;
    await step1ButtonClicked(this.aiSettings);
    this.creating = false;
  }

  async rebuild() {
    this.creating = true;

    if (!this.rebuildFromUrl) {
      alert("Please enter a URL to rebuild from.");
      this.creating = false;
      return;
    }

    const result = await step1RebuildFromUrl(this.rebuildFromUrl);
    console.log(result);
    console.log("Rebuild from URL completed");
    singularityStore.setSingularityLeftPanel("page_structure");
    this.creating = false;
  }

  nextStep() {
    if (this.activeStep < this.promptSteps.length - 1) {
      this.activeStep++;
    }
  }

  setSamplePrompt(prompt: SamplePrompt) {
    this.isBuildFromUrlMode = false;
    this.updateBusinessDescription(prompt.businessDescription);
    this.updateBusinessName(prompt.businessName);
  }

  selectRandomPrompt() {
    const randomPrompt =
      this.samplePrompts[Math.floor(Math.random() * this.samplePrompts.length)];

    this.setSamplePrompt(randomPrompt);
  }

  get businessName() {
    return this.aiSettings.title;
  }

  updateBusinessName(name: string) {
    this.aiSettings.title = name;
  }

  updateBusinessDescription(description: string) {
    this.aiSettings.description = description;
  }

  get businessDescription() {
    return this.aiSettings.description;
  }

  get shouldAutostart() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('autostart') === 'true';
  }
}
